/**
 * Generated, looping biome ambience: continuous beds driven by slow
 * modulation buffers, plus randomly scheduled wildlife and world events.
 */
import type { RideBiome } from '../biomes';
import { Graph } from './graph';
import type { SoundKit } from './kit';
import type { BirdSpecies } from './samples';
import type { OneShots, PlayOptions } from './voices';

const rand = (lo: number, hi: number) => lo + (hi - lo) * Math.random();
const pickOne = <T>(items: readonly T[]) => items[Math.floor(Math.random() * items.length) % items.length];
const variant = () => Math.floor(Math.random() * 12);

interface AmbientEvent { next: number; min: number; max: number; fire: (t: number) => void }

export class AmbienceScene {
  readonly output: GainNode;
  readonly g: Graph;
  private events: AmbientEvent[] = [];
  private disposed = false;
  constructor(ctx: BaseAudioContext, readonly kit: SoundKit, private shots: OneShots, destination: AudioNode, readonly biome: RideBiome) {
    this.g = new Graph(ctx, kit);
    this.output = this.g.gain(0, destination);
    BUILDERS[biome](this);
    this.output.gain.setTargetAtTime(1, ctx.currentTime, .5);
  }
  /** Schedule events that fall inside [from, until). Idempotent. */
  schedule(from: number, until: number) {
    if (this.disposed) return;
    for (const e of this.events) {
      if (e.next < from - .5) e.next = from + rand(0, e.min); // resume after a stall without a burst
      while (e.next < until) { e.fire(e.next); e.next += rand(e.min, e.max); }
    }
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    const t = this.g.ctx.currentTime;
    this.output.gain.cancelScheduledValues(t);
    this.output.gain.setTargetAtTime(0, t, .35);
    const first = this.g.sources[0];
    if (first) first.onended = () => { try { this.output.disconnect(); } catch { /* gone */ } };
    this.g.stop(t + 2.2);
  }

  // ---- builders
  event(min: number, max: number, fire: (t: number) => void, first = rand(min * .3, min)) {
    this.events.push({ next: this.g.ctx.currentTime + first, min, max, fire });
  }
  shot(buffer: AudioBuffer, o: PlayOptions) { this.shots.play(buffer, this.output, o); }
  /** Stereo band-passed wind with gusts. */
  wind(centre: number, q: number, level: number, gustRate = 1, depth = .4) {
    const g = this.g, k = this.kit, out = g.gain(level, this.output), am = g.modulated(1 - depth, depth, k.gust, gustRate);
    am.node.connect(out);
    const l = g.filter('bandpass', centre, q), r = g.filter('bandpass', centre * 1.1, q);
    g.loop(k.pink, 1, l); g.loop(k.pink, 1, r);
    g.stereo(l, r, .35, am.node);
    return out;
  }
  /** High rustle of leaves or grass riding on the gusts. */
  rustle(cutoff: number, level: number, gustRate = 1.3) {
    const g = this.g, out = g.gain(level, this.output), am = g.modulated(.4, .6, this.kit.gust, gustRate);
    am.node.connect(out);
    g.loop(this.kit.white, 1, g.filter('highpass', cutoff, .6, am.node));
  }
  /** Speech-shaped crowd murmur with syllabic babble. */
  crowd(level: number) {
    const g = this.g, out = g.gain(level, this.output), lp = g.filter('lowpass', 3200, .6, out), am = g.modulated(.72, .28, this.kit.babble, 1);
    am.node.connect(lp);
    const sum = g.gain(1, am.node);
    for (const [f, q, a] of [[620, .9, 1], [1500, 1.1, .6], [2700, 1.4, .24]] as const) g.loop(this.kit.pink, 1, g.filter('bandpass', f, q, g.gain(a, sum)));
  }
  /** Surf: swells open a lowpass and lift the level; a delayed wash follows each break. */
  surf(level: number) {
    const g = this.g, k = this.kit, c = g.ctx, out = g.gain(level, this.output);
    const chains = [0, 1].map(ch => {
      const am = g.gain(.5), lp = g.filter('lowpass', 700, .5, am);
      g.loop(k.pink, 1, lp);
      const swell = g.loop(k.swell, ch ? .93 : 1, g.gain(.5, am.gain), g.gain(1100, lp.frequency));
      return { am, swell };
    });
    g.stereo(chains[0].am, chains[1].am, .3, out);
    const wash = g.gain(.12, out), washAm = g.gain(.5, wash), delay = c.createDelay(4);
    delay.delayTime.value = 1.6;
    chains[0].swell.connect(delay); delay.connect(g.gain(.5, washAm.gain));
    g.loop(k.white, 1, g.filter('highpass', 2400, .6, washAm));
  }
  /** Pulsed cicada chorus around 5 kHz with slow swells. */
  cicadas(level: number) {
    const g = this.g, k = this.kit, out = g.gain(level, this.output), swell = g.modulated(.55, .45, k.gust, .45), pulse = g.gain(.5, swell.node);
    swell.node.connect(out);
    g.loop(k.white, 1, g.filter('bandpass', 5300, 4, pulse));
    g.osc(k.wave('cicada'), 148, g.gain(.5, pulse.gain));
  }
  /** Field crickets: a 4.6 kHz carrier gated by a three-pulse chirp envelope. */
  crickets(level: number) {
    const g = this.g, k = this.kit, out = g.gain(level, this.output), am = g.gain(k.envelopeMean('cricket'), out);
    g.osc('sine', 4600, am);
    g.osc(k.wave('cricket'), 1.45, am.gain);
  }
  /** Water lapping against the quay: dark, irregular swells. */
  lapping(level: number) {
    const g = this.g, k = this.kit, out = g.gain(level, this.output), am = g.modulated(.5, .5, k.texture, .32);
    am.node.connect(out);
    g.loop(k.brown, 1, g.filter('lowpass', 520, .7, am.node));
  }
  /** Muffled festival music from a distant stage. */
  music(level: number) {
    const g = this.g, out = g.gain(level, this.output);
    g.loop(this.kit.music, 1, g.filter('lowpass', 420, .7, out), g.filter('bandpass', 1100, 1, g.gain(.12, out)));
  }
  /** Bunting and canopies fluttering in the breeze. */
  flutter(level: number) {
    const g = this.g, out = g.gain(level, this.output), am = g.modulated(.3, .7, this.kit.texture, 1.9);
    am.node.connect(out);
    g.loop(this.kit.white, 1, g.filter('bandpass', 1500, .7, am.node));
  }
  birds(species: readonly BirdSpecies[], min: number, max: number, gain: [number, number]) {
    this.event(min, max, t => this.shot(this.kit.bird(pickOne(species), variant()), { at: t, gain: rand(...gain), pan: rand(-.85, .85), rate: rand(.96, 1.05), lowpass: rand(6000, 11000) }));
  }
  gulls(min: number, max: number, gain: [number, number]) {
    this.event(min, max, t => this.shot(this.kit.gull(variant()), { at: t, gain: rand(...gain), pan: rand(-.9, .9), rate: rand(.92, 1.08), lowpass: rand(4000, 9000) }));
  }
  cheers(min: number, max: number, gain: number) {
    this.event(min, max, t => this.shot(this.kit.cheer, { at: t, gain: gain * rand(.6, 1), rate: rand(.88, 1.05), pan: rand(-.4, .4), lowpass: 2600 }), rand(min * .5, max));
  }
  announcer(min: number, max: number, gain: number) {
    this.event(min, max, t => this.shot(this.kit.pa(variant()), { at: t, gain, pan: rand(-.3, .3), lowpass: 3400 }));
  }
}

const BUILDERS: Record<RideBiome, (s: AmbienceScene) => void> = {
  alpine: s => {
    s.wind(520, .45, .5, 1); s.rustle(3400, .045);
    s.birds(['finch', 'tit', 'robin'], 2.5, 8, [.08, .24]);
    s.event(14, 30, t => s.shot(s.kit.cowbell(variant()), { at: t, gain: rand(.03, .06), pan: rand(-.8, .8), lowpass: 2600 }));
  },
  canyon: s => {
    s.wind(250, 1.4, .5, .8, .5);
    const g = s.g, whistle = g.gain(0, s.output), am = g.modulated(.3, .3, s.kit.gust, 1.4), bp = g.filter('bandpass', 880, 22, am.node);
    am.node.connect(whistle); whistle.gain.value = 1; g.loop(s.kit.white, 1, bp); g.loop(s.kit.gust, 1.4, g.gain(140, bp.frequency));
    s.crowd(.2);
    s.announcer(8, 18, .07);
    s.cheers(18, 34, .1);
    s.event(24, 48, t => s.shot(s.kit.hawk(variant()), { at: t, gain: rand(.04, .08), pan: rand(-.8, .8), lowpass: 5000 }));
  },
  coast: s => {
    s.surf(.55); s.wind(700, .5, .18, 1.2);
    s.gulls(4, 12, [.06, .16]);
    s.cicadas(.09);
  },
  meadow: s => {
    s.wind(650, .5, .3, 1.1); s.rustle(2600, .03);
    s.birds(['lark', 'finch', 'warbler'], 1.8, 6, [.07, .2]);
    s.event(5, 13, t => s.shot(s.kit.bee(variant()), { at: t, gain: rand(.04, .09), rate: rand(.9, 1.1) }));
    s.crickets(.012);
  },
  harbour: s => {
    s.lapping(.22); s.wind(600, .6, .2, 1);
    s.event(.6, 2.2, t => s.shot(s.kit.waterSlap(variant()), { at: t, gain: rand(.06, .16), pan: rand(-.8, .8), rate: rand(.9, 1.1) }));
    s.gulls(3.5, 9, [.06, .16]);
    s.event(2.5, 7, t => {
      const mast = variant(), pan = [-.6, .15, .7][mast % 3];
      for (let k = 0, at = t; k < 1 + Math.floor(Math.random() * 4); k++, at += rand(.12, .5)) s.shot(s.kit.mast(mast), { at, gain: rand(.03, .07), pan, rate: rand(.985, 1.015) });
    });
    s.event(45, 80, t => s.shot(s.kit.foghorn, { at: t, gain: .05, pan: rand(-.5, .5), lowpass: 900 }), rand(20, 50));
  },
  park: s => {
    s.crowd(.3); s.music(.045); s.wind(600, .5, .14, 1); s.flutter(.02);
    s.cheers(12, 26, .12);
    s.announcer(18, 34, .045);
  },
};
